/**
 * TemplateWizardDialog — content-specific intake for Dynamic templates.
 *
 * Renders the template's `wizard_questions` as a field-type-aware form. On
 * submit the caller uses the answers to seed the Planning Agent chat; the
 * structured payload is persisted to the linked `content_briefs` row
 * (wizard_answers + intake_attachments + structured_sections) and
 * `content_pieces.metadata.selected_avatar_id`, so the Planning Agent can
 * read intake authoritatively from the DB.

 *
 * Static templates do NOT use this dialog — they push their structure
 * straight into the Draft Canvas.
 *
 * Wizard-answer persistence: in-flight answers are mirrored to localStorage
 * keyed by (pieceId, templateId) so dismissing the dialog mid-fill doesn't
 * lose progress. The key is cleared on successful submit, kept on Cancel.
 *
 * Audience Avatar is REQUIRED on every Dynamic template regardless of the
 * authored questions — it's the strongest voice-calibration signal reaching
 * both the Planning and Writing agents.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { UnifiedReferencePicker } from "./UnifiedReferencePicker";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  parseWizardQuestions,
  wizardFieldKind,
  type ReferenceChip,
  type WizardField,
  type WizardQuestion,
} from "@/lib/composerTemplates";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import type { Json } from "@/integrations/supabase/types";

/**
 * A single structured wizard answer. `value` shape depends on the field kind:
 *   text/date       → string
 *   choice (single) → string; choice (multi) → string[]
 *   number/slider   → number
 *   reference_picker→ ReferenceChip[]
 */
export type WizardAnswerValue = string | string[] | number | ReferenceChip[] | null;

export type TemplateWizardAnswer = {
  question: WizardQuestion;
  /** Legacy string form — kept for the current Composer.tsx seed builder. */
  answer: string;
  /** Structured value for DB persistence. */
  value: WizardAnswerValue;
};

export type WizardSubmitPayload = {
  answers: TemplateWizardAnswer[];
  selectedAvatarId: string;
  selectedAvatarName: string;
  intakeAttachments: ReferenceChip[];
};

type AvatarOption = { id: string; name: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  introMessage?: string | null;
  wizardQuestions: Json | null | undefined;
  /** Template's default_structure — snapshotted onto the Brief as `structured_sections`. */
  defaultStructure?: Json | null;
  /** Active Piece id — required for per-piece answer persistence. */
  pieceId?: string | null;
  /** Template id — required for per-template answer persistence. */
  templateId?: string | null;
  onSubmit: (payload: WizardSubmitPayload) => void;
};

const storageKey = (pieceId?: string | null, templateId?: string | null): string | null => {
  if (!pieceId || !templateId) return null;
  return `excerptus:wizard:${pieceId}:${templateId}`;
};

type StoredState = {
  values: Record<string, WizardAnswerValue>;
  avatarId?: string | null;
  attachments?: ReferenceChip[];
};

const loadState = (key: string | null): StoredState => {
  if (!key || typeof window === "undefined") return { values: {} };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { values: {} };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      // Back-compat: legacy shape was Record<string, string> directly.
      if (!("values" in parsed) && !("avatarId" in parsed) && !("attachments" in parsed)) {
        return { values: parsed as Record<string, WizardAnswerValue> };
      }
      return {
        values: (parsed.values ?? {}) as Record<string, WizardAnswerValue>,
        avatarId: typeof parsed.avatarId === "string" ? parsed.avatarId : null,
        attachments: Array.isArray(parsed.attachments) ? (parsed.attachments as ReferenceChip[]) : [],
      };
    }
    return { values: {} };
  } catch {
    return { values: {} };
  }
};

const saveState = (key: string | null, state: StoredState) => {
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Quota / private-browsing — silent.
  }
};

const clearState = (key: string | null) => {
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const stringifyValue = (value: WizardAnswerValue): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    // ReferenceChip[]?
    if (typeof value[0] === "object" && value[0] !== null && "kind" in value[0]) {
      return (value as ReferenceChip[]).map((c) => c.label).join(", ");
    }
    return (value as string[]).join(", ");
  }
  return "";
};

const isAnswered = (q: WizardQuestion, v: WizardAnswerValue): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (typeof v === "number") return Number.isFinite(v);
  if (Array.isArray(v)) return v.length > 0;
  return false;
};

const defaultValueFor = (field: WizardField | undefined): WizardAnswerValue => {
  const kind = field?.kind ?? "text";
  switch (kind) {
    case "text":
    case "date":
      return "";
    case "choice":
      return field?.kind === "choice" && field.multi ? [] : "";
    case "number":
      return "";
    case "slider":
      return field?.kind === "slider" ? field.min : 0;
    case "reference_picker":
      return [] as ReferenceChip[];
  }
};

export const TemplateWizardDialog = ({
  open,
  onOpenChange,
  templateName,
  introMessage,
  wizardQuestions,
  defaultStructure,
  pieceId,
  templateId,
  onSubmit,
}: Props) => {
  const { active } = useWorkspace();
  const workspaceId = active?.id ?? null;
  const questions = useMemo(() => parseWizardQuestions(wizardQuestions ?? null), [wizardQuestions]);
  const key = useMemo(() => storageKey(pieceId, templateId), [pieceId, templateId]);

  const [values, setValues] = useState<Record<string, WizardAnswerValue>>({});
  const [avatarId, setAvatarId] = useState<string>("");
  const [attachments] = useState<ReferenceChip[]>([]); // reserved: see note below
  const [avatars, setAvatars] = useState<AvatarOption[]>([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Hydrate localStorage state when the dialog opens.
  useEffect(() => {
    if (!open) return;
    const stored = loadState(key);
    setValues(stored.values);
    setAvatarId(stored.avatarId ?? "");
  }, [open, key]);

  // Fetch avatars for the active workspace.
  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;
    setLoadingAvatars(true);
    (async () => {
      const { data, error } = await supabase
        .from("content_avatars")
        .select("id, name")
        .eq("workspace_id", workspaceId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (cancelled) return;
      if (error) {
        toast.error(`Could not load avatars: ${error.message}`);
        setAvatars([]);
      } else {
        setAvatars((data ?? []) as AvatarOption[]);
      }
      setLoadingAvatars(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const persist = useCallback(
    (nextValues: Record<string, WizardAnswerValue>, nextAvatar: string) => {
      saveState(key, {
        values: nextValues,
        avatarId: nextAvatar || null,
        attachments,
      });
    },
    [key, attachments],
  );

  const updateValue = useCallback(
    (qid: string, val: WizardAnswerValue) => {
      setValues((prev) => {
        const next = { ...prev, [qid]: val };
        persist(next, avatarId);
        return next;
      });
    },
    [persist, avatarId],
  );

  const updateAvatar = useCallback(
    (id: string) => {
      setAvatarId(id);
      persist(values, id);
    },
    [persist, values],
  );

  const missingRequired = questions
    .filter((q) => q.required)
    .some((q) => !isAnswered(q, values[q.id] ?? defaultValueFor(q.field)));

  const missingAvatar = !avatarId;
  const canSubmit = !missingRequired && !missingAvatar && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !pieceId) {
      if (!pieceId) toast.error("No active Piece — cannot save wizard answers.");
      return;
    }
    setSubmitting(true);
    try {
      const answers: TemplateWizardAnswer[] = questions.map((q) => {
        const raw = values[q.id] ?? defaultValueFor(q.field);
        return {
          question: q,
          value: raw,
          answer: stringifyValue(raw),
        };
      });

      // Persist structured answers to the Piece so the Planning Agent can
      // read them authoritatively (not just from the chat seed).
      const structuredAnswers = answers.map(({ question, value }) => ({
        question_id: question.id,
        prompt: question.prompt,
        field_kind: wizardFieldKind(question),
        value,
      }));

      // Read current metadata so we merge, not overwrite.
      const { data: current, error: fetchErr } = await supabase
        .from("content_pieces")
        .select("metadata, active_brief_id, workspace_id")
        .eq("id", pieceId)
        .single();
      if (fetchErr) throw new Error(fetchErr.message);

      const currentMetadata =
        current?.metadata && typeof current.metadata === "object" && !Array.isArray(current.metadata)
          ? (current.metadata as Record<string, unknown>)
          : {};

      // Snapshot the template's default_structure into structured_sections so
      // the Writing Agent has per-section targets even before the Planning
      // Agent (which may refine them via SaveBriefPlan) runs.
      const structuredSections = Array.isArray(defaultStructure)
        ? (defaultStructure as Array<Record<string, unknown>>).map((s, idx) => ({
            id: typeof s.id === "string" ? s.id : `sec-${idx + 1}`,
            title: typeof s.section === "string" ? s.section : (typeof s.title === "string" ? s.title : `Section ${idx + 1}`),
            target_word_count: typeof s.target_word_count === "number" ? s.target_word_count : null,
            must_include: Array.isArray(s.must_include) ? s.must_include.filter((x) => typeof x === "string") : [],
            must_avoid: Array.isArray(s.must_avoid) ? s.must_avoid.filter((x) => typeof x === "string") : [],
            voice_lean: typeof s.voice_lean === "string" ? s.voice_lean : null,
          }))
        : [];

      // Only `selected_avatar_id` remains on metadata — intake_attachments now
      // lives on the linked content_briefs row (F.3 source of truth).
      const nextMetadata = {
        ...currentMetadata,
        selected_avatar_id: avatarId,
      };


      // Upsert the Brief row (F.3 source of truth) — create if missing.
      const workspaceIdForBrief = current?.workspace_id ?? workspaceId;
      if (workspaceIdForBrief) {
        let briefIdForPiece = current?.active_brief_id ?? null;
        if (!briefIdForPiece) {
          const { data: existingBrief } = await supabase
            .from("content_briefs")
            .select("id")
            .eq("workspace_id", workspaceIdForBrief)
            .eq("piece_id", pieceId)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          briefIdForPiece = existingBrief?.id ?? null;
        }
        const briefPayload = {
          workspace_id: workspaceIdForBrief,
          piece_id: pieceId,
          wizard_answers: structuredAnswers as unknown as Json,
          intake_attachments: attachments as unknown as Json,
          structured_sections: structuredSections as unknown as Json,
        };
        if (briefIdForPiece) {
          const { error: briefUpdErr } = await supabase
            .from("content_briefs")
            .update(briefPayload)
            .eq("id", briefIdForPiece);
          if (briefUpdErr) throw new Error(briefUpdErr.message);
        } else {
          const { data: inserted, error: briefInsErr } = await supabase
            .from("content_briefs")
            .insert(briefPayload)
            .select("id")
            .single();
          if (briefInsErr) throw new Error(briefInsErr.message);
          briefIdForPiece = inserted?.id ?? null;
        }
        // Point the piece at the freshly-updated brief.
        if (briefIdForPiece) {
          await supabase
            .from("content_pieces")
            .update({ active_brief_id: briefIdForPiece })
            .eq("id", pieceId);
        }
      }

      const { error: updateErr } = await supabase
        .from("content_pieces")
        .update({
          wizard_answers: structuredAnswers as unknown as Json,
          metadata: nextMetadata as unknown as Json,
        })
        .eq("id", pieceId);
      if (updateErr) throw new Error(updateErr.message);

      const selectedAvatarName = avatars.find((a) => a.id === avatarId)?.name ?? "Avatar";
      clearState(key);
      onSubmit({
        answers,
        selectedAvatarId: avatarId,
        selectedAvatarName,
        intakeAttachments: attachments,
      });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{templateName}</DialogTitle>
          {introMessage ? (
            <DialogDescription className="whitespace-pre-wrap">{introMessage}</DialogDescription>
          ) : (
            <DialogDescription>
              Answer a few content-specific questions. Your answers seed the Planning Agent
              and are saved with the Piece so both agents work from the same intake.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">
          {/* Always-required Audience Avatar picker. */}
          <div className="space-y-1.5 rounded-md border border-primary/30 bg-primary/5 p-3">
            <Label htmlFor="wq-avatar" className="text-sm">
              Audience Avatar<span className="ml-1 text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              The Planning and Writing Agents use this to calibrate voice and framing.
            </p>
            {loadingAvatars ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading avatars…
              </div>
            ) : avatars.length === 0 ? (
              <p className="text-xs text-destructive">
                No avatars found for this workspace. Create one in the Codex first.
              </p>
            ) : (
              <Select value={avatarId} onValueChange={updateAvatar}>
                <SelectTrigger id="wq-avatar">
                  <SelectValue placeholder="Pick an audience avatar" />
                </SelectTrigger>
                <SelectContent>
                  {avatars.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {questions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This template has no additional wizard questions.
            </p>
          ) : (
            questions.map((q) => (
              <WizardFieldRow
                key={q.id}
                question={q}
                value={values[q.id] ?? defaultValueFor(q.field)}
                onChange={(v) => updateValue(q.id, v)}
                workspaceId={workspaceId}
              />
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Continue to Planning Agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Per-field renderer ----------

const WizardFieldRow = ({
  question,
  value,
  onChange,
  workspaceId,
}: {
  question: WizardQuestion;
  value: WizardAnswerValue;
  onChange: (v: WizardAnswerValue) => void;
  workspaceId: string | null;
}) => {
  const field = question.field ?? { kind: "text" as const, multiline: true };
  const id = `wq-${question.id}`;
  const label = (
    <Label htmlFor={id} className="text-sm">
      {question.prompt}
      {question.required ? <span className="ml-1 text-destructive">*</span> : null}
    </Label>
  );

  let control: ReactElement;
  switch (field.kind) {
    case "text":
      control = field.multiline === false ? (
        <Input
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.hint ?? ""}
        />
      ) : (
        <Textarea
          id={id}
          rows={2}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.hint ?? ""}
        />
      );
      break;

    case "choice":
      if (field.multi) {
        const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
        control = (
          <div className="flex flex-wrap gap-3">
            {field.options.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={selected.has(opt)}
                  onCheckedChange={() => {
                    const next = new Set(selected);
                    if (next.has(opt)) next.delete(opt);
                    else next.add(opt);
                    onChange(Array.from(next));
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      } else {
        control = (
          <RadioGroup
            value={typeof value === "string" ? value : ""}
            onValueChange={(v) => onChange(v)}
            className="flex flex-wrap gap-3"
          >
            {field.options.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm">
                <RadioGroupItem value={opt} id={`${id}-${opt}`} />
                {opt}
              </label>
            ))}
          </RadioGroup>
        );
      }
      break;

    case "number":
      control = (
        <Input
          id={id}
          type="number"
          min={field.min}
          max={field.max}
          value={
            typeof value === "number" ? value : typeof value === "string" ? value : ""
          }
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") onChange("");
            else onChange(Number(raw));
          }}
          placeholder={field.unit ? `Number (${field.unit})` : question.hint ?? ""}
        />
      );
      break;

    case "slider": {
      const current =
        typeof value === "number" ? value : typeof field.min === "number" ? field.min : 0;
      control = (
        <div className="grid gap-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{field.minLabel ?? field.min}</span>
            <span className="font-medium text-foreground">{current}</span>
            <span>{field.maxLabel ?? field.max}</span>
          </div>
          <Slider
            id={id}
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            value={[current]}
            onValueChange={(v) => onChange(v[0] ?? current)}
          />
        </div>
      );
      break;
    }

    case "date":
      control = (
        <Input
          id={id}
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;

    case "reference_picker": {
      const chips: ReferenceChip[] = Array.isArray(value)
        ? (value as unknown[]).filter(
            (v): v is ReferenceChip =>
              !!v && typeof v === "object" && "kind" in (v as object) && "id" in (v as object),
          )
        : [];
      control = (
        <UnifiedReferencePicker
          workspaceId={workspaceId}
          sources={field.sources}
          multi={field.multi !== false}
          value={chips}
          onChange={(next) => onChange(next)}
        />
      );
      break;
    }
  }

  return (
    <div className="space-y-1.5">
      {label}
      {control}
      {question.hint && field.kind !== "text" ? (
        <p className="text-xs text-muted-foreground">{question.hint}</p>
      ) : null}
    </div>
  );
};

export default TemplateWizardDialog;
